import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { fabric } from 'fabric';
import ImagePaletteBox from './ImagePaletteBox';
import './ProductColor.css';
import sign from '../assets/nofill.png';

const ImagePaletteComponent = ({
  initialPaletteColors,
  canvas,
  setSingleColor,
  setIsPng,
  removeBackground,
  calculatePrice,
  handleColorReset,
  addToHistory,
  saveCanvasDataToLocalStorage,
  activeTab,
  dominantColors
}) => {
  const [paletteColors, setPaletteColors] = useState(initialPaletteColors || []);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [currentColor, setCurrentColor] = useState(null);
  const [dominantColor, setDominantColor] = useState(null);

  // ---- Original pins (per image originKey) ----
  const originalPaletteRef = useRef(null);   // frozen palette from the first time
  const originalImageBlobRef = useRef(null); // the *true* original image blob to send to API
  const originKeyRef = useRef(null);         // current image's stable origin key

  // Maps to persist original src/blob across re-renders & replacements
  const firstSrcByOriginRef = useRef({});    // { [originKey]: firstSeenURL }
  const firstBlobByOriginRef = useRef({});   // { [originKey]: Blob }

  // Map: "#OLDHEX" -> "#NEWHex" | "transparent"
  const [colorMap, setColorMap] = useState({});

  // ---------- Effects ----------
  useEffect(() => {
    setPaletteColors(initialPaletteColors);
    const activeObject = canvas?.getActiveObject?.();
    if (activeObject && activeObject.dominant_color) {
      setDominantColor(activeObject.dominant_color);
    }
  }, [initialPaletteColors, canvas]);

  useEffect(() => {
    setPaletteColors(initialPaletteColors);
  }, [initialPaletteColors]);

  // Pre-capture original on first mount / when image changes
  useEffect(() => {
    const activeObject = canvas?.getActiveObject?.();
    if (activeObject) {
      // fire & forget
      ensureOriginalPinned(activeObject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas, initialPaletteColors]);

  // ---------- UI helpers ----------
  const handleColorClick = (color, index) => {
    setCurrentColor(color);
    setCurrentIndex(index);
    setShowColorPicker(true);
  };

  const removeBgColor = async (activeObject, value) => {
    try {
      if (activeObject && activeObject.type === 'image') {
        if (value) {
          const imgSrc = activeObject.getSrc();
          const domColor = await loadImageFromUrl(imgSrc);
          const removeColorFilter = new fabric.Image.filters.RemoveColor({
            distance: 0.2,
            color: domColor,
            alpha: 1
          });
          activeObject.filters.push(removeColorFilter);
          activeObject.applyFilters();
          activeObject.set({ bgRemove: true });
        } else {
          const filters = activeObject.filters;
          if (filters && filters.length > 0) {
            for (let i = filters.length - 1; i >= 0; i--) {
              if (filters[i] instanceof fabric.Image.filters.RemoveColor) {
                filters.splice(i, 1);
              }
            }
          }
          activeObject.applyFilters();
          activeObject.set({ bgRemove: false });
        }
        canvas.renderAll();
      }
    } catch (error) {
      console.error('Error in removeBgColor:', error);
    }
  };

  const loadImageFromUrl = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = function () {
        const c = document.createElement('canvas');
        const ctx = c.getContext('2d');
        c.width = img.width;
        c.height = img.height;
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, c.width, c.height);
        const data = imageData.data;
        const localMap = {};
        let maxCount = 0;
        let domColor = [0, 0, 0];
        for (let i = 0; i < data.length; i += 4) {
          const color = [data[i], data[i + 1], data[i + 2]];
          const key = color.join(',');
          localMap[key] = (localMap[key] || 0) + 1;
          if (localMap[key] > maxCount) {
            maxCount = localMap[key];
            domColor = color;
          }
        }
        resolve(`rgb(${domColor.join(',')})`);
      };
      img.onerror = reject;
      img.src = url;
    });

  const loadImageToBase64 = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = function () {
        const c = document.createElement('canvas');
        const ctx = c.getContext('2d');
        c.width = img.width;
        c.height = img.height;
        ctx.drawImage(img, 0, 0);
        resolve(c.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = url;
    });

  const colorsMatch = (c1, c2, tol = 10) =>
    Math.abs(c1[0] - c2[0]) <= tol &&
    Math.abs(c1[1] - c2[1]) <= tol &&
    Math.abs(c1[2] - c2[2]) <= tol;

  const handleCloseColorPicker = () => setShowColorPicker(false);

  // ----- color helpers -----
  const bgrToHex = (bgrArr) => {
    const [b, g, r] = bgrArr;
    const to2 = (n) => n.toString(16).padStart(2, '0').toUpperCase();
    return `#${to2(r)}${to2(g)}${to2(b)}`;
  };

  const rgbaToHexOrTransparent = ({ r, g, b, a }) => {
    // Always treat a===0 as 'transparent' (avoid '#00000000' issues server-side)
    if (a === 0) return 'transparent';
    const to2 = (n) => n.toString(16).padStart(2, '0').toUpperCase();
    return `#${to2(r)}${to2(g)}${to2(b)}`;
  };

  // ---------- ORIGINAL PINNING (the fix) ----------
  const getStableOriginKey = (obj) => {
    if (!obj.originKey) {
      if (!obj.id) {
        obj.id = `img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      }
      obj.originKey = `origin-${obj.id}`;
    }
    return obj.originKey;
  };

  const ensureOriginalPinned = async (activeObject) => {
    if (!activeObject) return;

    const originKey = getStableOriginKey(activeObject);
    if (originKeyRef.current !== originKey) {
      originKeyRef.current = originKey;
      originalImageBlobRef.current = null;
      originalPaletteRef.current = null;
      setColorMap({});
    }

    // Prefer an already-pinned blob on the object
    if (activeObject.originalImageBlob instanceof Blob) {
      originalImageBlobRef.current = activeObject.originalImageBlob;
    }

    // Or a previously pinned blob for this origin
    if (
      !originalImageBlobRef.current &&
      firstBlobByOriginRef.current[originKey] instanceof Blob
    ) {
      originalImageBlobRef.current = firstBlobByOriginRef.current[originKey];
    }

    // Or a fresh upload held on the object
    if (!originalImageBlobRef.current && activeObject?.imageFile instanceof Blob) {
      originalImageBlobRef.current = activeObject.imageFile;
      firstBlobByOriginRef.current[originKey] = activeObject.imageFile;
    }

    // If we still don't have a blob, pin the first URL we can and fetch it
    if (!originalImageBlobRef.current) {
      if (!firstSrcByOriginRef.current[originKey]) {
        const firstUrl =
          activeObject.originalImagePath || // <— prefer pre-saved original path
          activeObject.originalSrc ||
          activeObject.newImagePath ||
          (typeof activeObject.getSrc === 'function' ? activeObject.getSrc() : activeObject.src);

        if (firstUrl) firstSrcByOriginRef.current[originKey] = firstUrl;
      }

      const stableFirstUrl = firstSrcByOriginRef.current[originKey];
      if (stableFirstUrl) {
        const res = await fetch(stableFirstUrl, { mode: 'cors' });
        const blob = await res.blob();
        originalImageBlobRef.current = blob;
        firstBlobByOriginRef.current[originKey] = blob;

        // keep a stable path too so we can rehydrate after JSON -> fabric
        if (!activeObject.originalImagePath) {
          activeObject.originalImagePath = stableFirstUrl;
        }
      }
    }

    // 🔐 freeze original palette ONCE and store it on the object
    if (!activeObject.originalPalette && initialPaletteColors?.length) {
      activeObject.originalPalette = initialPaletteColors.map((c) => [...c]); // deep-ish copy
    }

    // keep a ref too (for convenience)
    if (!originalPaletteRef.current && activeObject.originalPalette?.length) {
      originalPaletteRef.current = activeObject.originalPalette;
    }

    // mirror pinned blob back to the object (survives in-memory; not serialized)
    if (originalImageBlobRef.current && !activeObject.originalImageBlob) {
      activeObject.originalImageBlob = originalImageBlobRef.current;
    }

    // also record path for future rehydration if missing
    if (
      !activeObject.originalImagePath &&
      firstSrcByOriginRef.current[originKey]
    ) {
      activeObject.originalImagePath = firstSrcByOriginRef.current[originKey];
    }
  };

  // ---------- Recolor ----------
  const handleNewColorChange = async (newColor) => {
    try {
      const activeObject = canvas.getActiveObject();
      if (!activeObject) return;

      // Ensure original blob & original palette are pinned for THIS image
      await ensureOriginalPinned(activeObject);

      // Source of truth for "oldColor"
      const originalPalette =
        activeObject.originalPalette ||
        originalPaletteRef.current ||
        initialPaletteColors ||
        [];
      if (!originalPalette.length) {
        console.warn('Original palette not available.');
        return;
      }

      const idx = currentIndex;
      const oldBGR_ORIG = originalPalette[idx];
      if (!oldBGR_ORIG) {
        console.warn('No original color at index:', idx);
        return;
      }

      // Convert the newly picked color to a hex or "transparent"
      const newHexOrTransp = rgbaToHexOrTransparent({
        r: newColor.r,
        g: newColor.g,
        b: newColor.b,
        a: typeof newColor.a === 'number' ? newColor.a : 1
      });

      // ---- Build colors[] so every index uses the CURRENT visible color ----
      const colorsArr = originalPalette.map((obgr, i) => {
        const oldHex = bgrToHex(obgr);

        let newHex;
        if (i === idx) {
          // just chosen color
          newHex = newHexOrTransp;
        } else if (paletteColors?.[i]) {
          const pc = paletteColors[i]; // [B,G,R,(A)]
          if (typeof pc[3] === 'number' && pc[3] === 0) {
            newHex = 'transparent';
          } else {
            newHex = bgrToHex([pc[0], pc[1], pc[2]]);
          }
        } else if (colorMap[oldHex]) {
          // fallback to any prior mapping kept in state
          newHex = colorMap[oldHex];
        } else {
          // unchanged
          newHex = oldHex;
        }

        return { oldColor: oldHex, newColor: newHex };
      });

      // Keep a fresh mapping in state (reflect what we are sending)
      const nextMap = {};
      colorsArr.forEach((p) => {
        nextMap[p.oldColor] = p.newColor;
      });
      setColorMap(nextMap);

      // ---- Always send the TRUE ORIGINAL blob ----
      if (!(originalImageBlobRef.current instanceof Blob)) {
        throw new Error('Original image blob was not pinned.');
      }

      const form = new FormData();
      form.append('original_image', originalImageBlobRef.current, 'original.png');
      form.append(
        'json',
        JSON.stringify({
          colors: colorsArr,
          session_id: activeObject.session_id || 'default',
          delta_e_thresh: 15,
          mode: 'flat'
        })
      );

      const resp = await axios.post('http://127.0.0.1:5055/recolor_one', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const relPath = resp?.data?.path;
      if (!relPath) throw new Error('No path in response');
      const newImageUrl = `http://127.0.0.1:5055/${relPath}`;
      const base64 = await loadImageToBase64(newImageUrl);

      // Update canvas with new image but keep origin + original blob & original palette pinned
      fabric.Image.fromURL(base64, (img) => {
        img.colorChanged = true;
        img.set({
          src: newImageUrl,
          top: activeObject.top,
          left: activeObject.left,
          angle: activeObject.angle,
          scaleX: activeObject.scaleX,
          scaleY: activeObject.scaleY,
          flipX: activeObject.flipX,
          flipY: activeObject.flipY,
          opacity: activeObject.opacity,
          id: activeObject.id,
          bgRemove: activeObject.bgRemove,
          filters: activeObject.filters,
          img: true,
          origX: activeObject.origX,
          origY: activeObject.origY,
          dominant_color: activeObject.dominant_color,
          makeOneColorCode: activeObject.makeOneColorCode,
          makeSingleColor: activeObject.makeSingleColor,
          makeSingleColorInvert: activeObject.makeSingleColorInvert,
          makeOneColor: activeObject.makeOneColor,
          paletteColors: activeObject.paletteColors,
          session_id: activeObject.session_id,
          imageFile: activeObject.imageFile,
          imageType: activeObject.imageType,
          quantized_image: activeObject.quantized_image,
          selectedColorIndex: idx,
          designElement: activeObject.designElement,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high',
          originKey: activeObject.originKey || activeObject.id || activeObject.src
        });

        // 🔐 carry pinned originals forward
        img.originalImageBlob =
          activeObject.originalImageBlob || originalImageBlobRef.current;
        img.originalPalette = activeObject.originalPalette || originalPalette;

        // also persist the original *path* for rehydration after JSON -> fabric
        img.originalImagePath =
          activeObject.originalImagePath ||
          firstSrcByOriginRef.current[originKeyRef.current] ||
          null;

        // Update visible palette (UI only) for clicked index; others stay as-is
        const updatedPalette = [...paletteColors];
        if (newHexOrTransp === 'transparent') {
          // show same base color but alpha 0 (keeps index identity in UI)
          updatedPalette[idx] = [oldBGR_ORIG[0], oldBGR_ORIG[1], oldBGR_ORIG[2], 0];
        } else {
          const r = parseInt(newHexOrTransp.slice(1, 3), 16);
          const g = parseInt(newHexOrTransp.slice(3, 5), 16);
          const b = parseInt(newHexOrTransp.slice(5, 7), 16);
          updatedPalette[idx] = [b, g, r, 255]; // BGR + opaque
        }
        img.paletteColors = updatedPalette;
        setPaletteColors(updatedPalette);

        if (img.bgRemove) removeBgColor(img, true);

        canvas.remove(activeObject);
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
        calculatePrice(canvas);

        img.setControlVisible('ml', false);
        img.setControlVisible('mb', false);
        img.setControlVisible('mr', false);
        img.setControlVisible('mt', false);

        saveCanvasDataToLocalStorage(activeTab);
      });

      // setShowColorPicker(false);
    } catch (error) {
      console.error('Error changing color via recolor_one:', error);
    }
  };

  return (
    <div className="image-palette">
    <div className="palette-colors outline-main">
  {paletteColors &&
    paletteColors.slice(0, 5).map((color, index) => (
      <div key={index} className="color-box-container">
        <div
          className="color-box"
          style={{
            backgroundColor: `rgba(${color[2]}, ${color[1]}, ${color[0]}, ${
              typeof color[3] === 'number' ? color[3] / 255 : 1
            })`
          }}
          onClick={() => handleColorClick(color, index)}
        >
          {removeBackground &&
            dominantColors &&
            colorsMatch(dominantColors, [color[2], color[1], color[0]], 2) && (
              <span className="cross-icon">&#10060;</span>
            )}
          {color[3] === 0 && (
            <div className="transparent-overlay">
              <img src={sign} alt="no fill" style={{ height: '100%', width: '100%' }} />
            </div>
          )}
        </div>
      </div>
    ))}

  {/* Show a preview if more than 6 */}
  {paletteColors.length > 5 && (
    <div className="">
      <div className="">
        +{paletteColors.length - 5} more
      </div>
    </div>
  )}
</div>


{showColorPicker && (
  <ImagePaletteBox
    onColorChange={handleNewColorChange}
    onClose={() => setShowColorPicker(false)}
    currentColor={currentColor}
    setSingleColor={setSingleColor}
    handleColorReset={handleColorReset}

    // ✅ frozen original palette (never changes)
    originalImagePalette={
      originalPaletteRef.current ||
      canvas?.getActiveObject?.()?.originalPalette ||
      initialPaletteColors ||
      []
    }

    // ✅ live/current palette (reflects recolor)
    currentPalette={paletteColors}

    dominantColorBGRA={dominantColor}
    removeBackground={removeBackground}
    onPickOriginalIndex={(idx) => setCurrentIndex(idx)}
    selectedOriginalIndex={currentIndex}
  />
)}



    </div>
  );
};

export default ImagePaletteComponent;
